export function debounce(fn, wait = 300) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

export async function fetchTextWithTimeout(input, { timeout = 8000, signal, ...init } = {}) {
  const controller = new AbortController();
  const timeoutMs = Number.isFinite(timeout) && timeout >= 0 ? timeout : 8000;
  let abortReason;
  const abortRequest = (reason) => {
    abortReason = reason;
    controller.abort(reason);
  };
  const abortFromCaller = () => abortRequest(signal.reason || abortError());

  if (signal?.aborted) {
    abortFromCaller();
  } else {
    signal?.addEventListener('abort', abortFromCaller, { once: true });
  }

  const timeoutId = setTimeout(() => {
    abortRequest(timeoutError());
  }, timeoutMs);

  try {
    const response = await fetch(input, { ...init, signal: controller.signal });
    const text = await response.text();
    return { response, text };
  } catch (error) {
    throw abortReason || error;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

export function getCartSections() {
  const sections = [];
  const el = document.querySelector('scale-cart-drawer, scale-cart-page');
  if (el?.dataset.sectionId) sections.push(el.dataset.sectionId);
  return sections;
}

function abortError() {
  return new DOMException('The request was aborted.', 'AbortError');
}

function timeoutError() {
  return new DOMException('The request timed out.', 'TimeoutError');
}

// ── Shop wall clock ────────────────────────────────────────────────────────
// Liquid renders `'now' | date: '%z'` in the shop's time zone ("+0200") while
// the visitor's browser runs in its own. Every deadline a merchant configures
// (countdown end, dispatch cut-off) is shop-local wall time, so consumers go
// through this offset instead of trusting `new Date()` local fields.
// Wall-clock Dates below carry the shop's fields in their UTC getters.

export const DISPATCH_DAYS = Object.freeze({
  weekdays: [1, 2, 3, 4, 5],
  monday_to_saturday: [1, 2, 3, 4, 5, 6],
  every_day: [0, 1, 2, 3, 4, 5, 6],
});

export function parseUtcOffset(value) {
  const match = /^([+-])(\d{2}):?(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

/** Comma-separated offsets, one per day ahead, so a DST switch inside the horizon is honoured. */
export function parseUtcOffsets(value) {
  const offsets = String(value ?? '').split(',').map(parseUtcOffset);
  return offsets.length && offsets.every((offset) => offset !== null) ? offsets : null;
}

export function parseClockTime(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function parseWallClockDate(value) {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const [, year, month, day, hours, minutes] = match.map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hours > 23 || minutes > 59) return null;
  const wall = new Date(Date.UTC(year, month - 1, day, hours, minutes));
  if (wall.getUTCMonth() !== month - 1 || wall.getUTCDate() !== day) return null;
  return wall;
}

export function shopWallClock(offsetMinutes, now = Date.now()) {
  return new Date(now + offsetMinutes * 60000);
}

export function wallClockToEpoch(wall, offsetMinutes) {
  return wall.getTime() - offsetMinutes * 60000;
}

/**
 * Next order cut-off strictly after `now`, on a dispatch day, in shop time.
 * `utcOffsets[n]` is the shop offset n days ahead (the last one repeats).
 * @returns {{ epoch: number, wall: Date, daysAhead: number } | null}
 */
export function nextDispatchCutoff({ cutoffMinutes, dispatchDays, utcOffsets, now = Date.now() }) {
  const days = DISPATCH_DAYS[dispatchDays];
  if (!days || cutoffMinutes === null || !utcOffsets?.length) return null;

  const wallNow = shopWallClock(utcOffsets[0], now);
  for (let ahead = 0; ahead <= 7; ahead++) {
    const wall = new Date(Date.UTC(
      wallNow.getUTCFullYear(),
      wallNow.getUTCMonth(),
      wallNow.getUTCDate() + ahead,
      Math.floor(cutoffMinutes / 60),
      cutoffMinutes % 60
    ));
    const offsetMinutes = utcOffsets[Math.min(ahead, utcOffsets.length - 1)];
    const epoch = wallClockToEpoch(wall, offsetMinutes);
    if (!days.includes(wall.getUTCDay()) || epoch <= now) continue;
    return { epoch, wall, daysAhead: ahead };
  }
  return null;
}
